import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnChanges, SimpleChanges } from '@angular/core';
import { IPageDataV2, IPageDataV3, IPageDataV4 } from '@df/catalog/cms/cms-model.interface';
import { ImageModule } from '@df/module-image/image.module';
import { RatioOptions } from '@df/ui/atomic/atomic-options';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { SocialService } from '@df/ui/social/social.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { copyToClipboard } from '@df/util/fn/copy-to-clipboard';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { NICmsContentComponent } from '@icms/core/content';
import { NICmsContentField } from '@icms/core/content/interfaces/icms-content-field.interfaces';
import { ICmsPageType } from '@icms/core/content/page/icms-page-type.decorator';
import { ICmsDataChangeService } from '@icms/core/data/change/icms-data-change.service';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { BlogNewsletterLinkComponent } from '../../../../catalog/page/component/blog-newsletter/blog-newsletter-link.component';
import { LocalICmsPage } from '../../../../catalog/page/local/local-page';
import { PageViewAbstract } from '../../../../catalog/page/view/page-view.abstract';
import { PageHeaderService } from '../../../../layout/page/header/page-header.service';
import { NBlogCategoryPageType } from '../category-view/cms-blog-page-category-view.component';
import { BLOG_TAB } from '../root-view/cms-blog-page-root-view.component';

declare module '@df/catalog/cms/cms-model.interface' {
    export interface IPageDataV3 {
        components: NICmsContentComponent.IData[];
        additional: ICmsAdditionalData
    }
}

export interface ICmsAdditionalData {
    headerTransparent?: boolean;
    headerLightMode?: boolean;
    headerHideUp?: boolean;
    story_thumbnail?: string;
}

export interface ICmsAdditionalMeta {
    [key: string]: any;
}
export namespace NBlogPostPageType {
    export const typeId = 'blog-post';

    export interface ITypeData {
        tags?: number[];
        author?: string;
        date?: Date;
        readingTime?: string;
    }

    export interface IQuickLinks {
        name: string;
        url: string;
    }
}

@ICmsPageType({
    id: NBlogPostPageType.typeId,
    title: 'Blog post',
    fields: [
        {
            type: NICmsContentField.EFieldType.image,
            key: 'typeData.image',
            title: 'Image',
            tab: BLOG_TAB
        },
        <NICmsContentField.IChoiceField>{
            key: 'typeData.ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-16-9',
            tab: BLOG_TAB
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'typeData.author',
            title: 'Post author',
            tab: BLOG_TAB
        },
        {
            type: NICmsContentField.EFieldType.date,
            key: 'typeData.date',
            title: 'Post date',
            tab: BLOG_TAB
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'typeData.reading_time',
            title: 'Reading time',
            default: '3m',
            tab: BLOG_TAB
        },
        <NICmsContentField.IRelationField<IPageDataV4, ICmsPage>>{
            key: 'typeData.tags',
            type: NICmsContentField.EFieldType.relation,
            title: 'Additional blog tags',
            options: {
                title: 'Select page',
                type: 'page',
                multiple: true,
                filter: (model: ICmsPage, data: IPageDataV4) => model?.rawData?.pageType === NBlogCategoryPageType.typeId
            },
            tab: BLOG_TAB
        }
    ],
    /**
     * Only allow parent page to be blog-category
     */
    parentFilter: page => {
        return page.rawData?.pageType === NBlogCategoryPageType.typeId;
    }
})
@Component({
    selector: 'cms-blog-page-post-view',
    templateUrl: './cms-blog-page-post-view.html',
    styleUrls: ['./cms-blog-page-post-view.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [BlogNewsletterLinkComponent, TranslationModule, ICmsClientModule, CommonModule, ImageModule, SizeClassDirective]
})
export class CmsBlogPagePostViewComponent extends PageViewAbstract implements OnChanges {
    /**
     * Blog tag categories
     */
    tagCategories?: LocalICmsPage<IPageDataV2>[];

    showSocial = false;

    quickLinks: NBlogPostPageType.IQuickLinks[] = [
        { name: 'New Arrivals', url: '/new-in' },
        { name: 'Interior Trends', url: '/interior-trends' },
        { name: 'Customer Homes', url: '/my-barkerandstonehouse' },
        { name: 'Follow us on Instagram ', url: 'https://www.instagram.com/barkerandstonehouse/' }
    ];

    constructor(
        iCmsDataChangeService: ICmsDataChangeService,
        pageHeaderService: PageHeaderService,
        private socialService: SocialService,
        protected _ref: ChangeDetectorRef
    ) {
        super(iCmsDataChangeService, pageHeaderService);
    }

    fbShare() {
        return this.model && this.socialService.fbShare(this.model);
    }

    twShare() {
        return this.model && this.socialService.twShare(this.model);
    }

    pinShare() {
        return this.model && this.socialService.pinShare(this.model);
    }

    copyUrlToClipboard() {
        copyToClipboard(window.location.href);
    }

    get typeData() {
        return this.model?.data?.typeData;
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['model']) {
            this.tagCategories = (this.model as LocalICmsPage<IPageDataV3>)?.getBlogPostCategories()?.slice(0, 3);
            super.ngOnChanges(changes);
        }
    }

    toogleSocialShare() {
        this.showSocial = !this.showSocial;
        this._ref.detectChanges();
    }
}
